/**
 * "Who is signing up" — the newest books in the range, with what they bought.
 *
 * ⚠️ THERE IS NO CITY COLUMN, and there must not be one. The prototype shows
 * "MJ2027 · Austin, TX"; the wedding schema has no city, state or country field
 * anywhere, so that column could only ever be invented. The wedding code stands
 * alone under the couple's names.
 *
 * SOURCE IS NULL UNTIL MIGRATION 1111. `wedding_books.acquisition_source` does
 * not exist in production yet, so every row reads '—' and the card says once,
 * underneath, why — rather than nine dashes with no explanation.
 */

import React from 'react';
import {
  Box, Card, CardContent, Link, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { WeddingTierChip } from '../WeddingChips';
import { centsToUsd, num, relativeTime } from '../weddingFormat';
import type { NewestCoupleRow } from '../weddingApi';

interface Props {
  rows: NewestCoupleRow[] | undefined;
  acquisitionAvailable: boolean | undefined;
  acquisitionReason: string | null | undefined;
  rangeLabel: string;
}

/**
 * The wizard's answers, spelled the way a person says them. An unknown value is
 * shown verbatim rather than swallowed — a source we did not anticipate is
 * information, not an error.
 */
const SOURCE_LABEL: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  friends_book: "A friend's book",
  planner_referral: 'Planner referral',
  google_search: 'Google search',
  other: 'Other',
};

function sourceLabel(row: NewestCoupleRow): string | null {
  if (!row.acquisition_source) return null;
  return SOURCE_LABEL[row.acquisition_source] || row.acquisition_source;
}

const NewestCouplesCard: React.FC<Props> = ({
  rows, acquisitionAvailable, acquisitionReason, rangeLabel,
}) => {
  const navigate = useNavigate();
  const couples = rows || [];

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Who is signing up
          </Typography>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => navigate('/dashboard/wedding/members')}
          >
            All members →
          </Link>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Newest couples in {rangeLabel}, where they came from, and what they bought.
        </Typography>

        {couples.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No couple started a book in this range.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Couple</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Package</TableCell>
                <TableCell align="right">Guests invited</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {couples.map((row) => {
                const label = sourceLabel(row);
                const invited = num(row.guests_invited);
                const total = num(row.guests_total);
                return (
                  <TableRow
                    key={row.book_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/dashboard/wedding/books/${row.book_id}`)}
                  >
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.partner1_name} &amp; {row.partner2_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.wedding_code}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {label ? (
                        <Typography variant="body2">
                          {label}
                          {row.acquisition_detail ? ` · ${row.acquisition_detail}` : ''}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell><WeddingTierChip tier={row.tier} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title={`${total} on the guest list`}>
                        <Typography variant="body2">{invited.toLocaleString()}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{centsToUsd(row.paid_cents)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {relativeTime(row.created_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {acquisitionAvailable === false && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            {acquisitionReason
              || 'Couples are not yet asked how they heard about LifeScribe, so every source reads “—”.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default NewestCouplesCard;
